// ─────────────────────────────────────────────────────────────────────────────
// graphRoutes — where an OS entity can actually be OPENED, and how.
//
// THIS APP HAS NO URL ROUTER. That is the single fact this module exists to
// respect. Navigation is App-level state — `screen: 'home' | 'domain' | 'agent'`
// with `activeDomain` / `activeAgent` (App.tsx) — so a graph node's openTarget
// cannot be a path, and manufacturing one (`/domains/trading`) would name a page
// that does not exist. What is written here is the app's OWN address form:
//
//     home                     → screen 'home'      (RexyHomepage)
//     domain:TRADING           → screen 'domain'    (DomainDashboardScreen)
//     agent:TRADING/ARTHUR     → screen 'agent'     (AgentViewScreen)
//
// A route is therefore a NAV INTENT, resolved by whoever owns App state. The
// graph never calls window.location.href, never remounts the document and never
// leaves the SPA — it hands an intent to a callback and the existing handlers do
// what they already do.
//
// THE FRONTEND IS THE AUTHORITY FOR ROUTES AND ONLY FOR ROUTES. The runtime
// registry (core/agent_registry.py) holds no route and no colour, so per the
// G2.0 authoritative-source rule presentation data is sourced here — from
// lib/agents, the same roster the DomainBar and DomainDashboardScreen render
// from. Identity and parenthood are NEVER taken from here; they come from the
// live structural endpoint. This module reads lib/agents for REACHABILITY, and
// `accent` is exported only for the parity report, never for the renderer —
// graphPalette remains the graph's sole colour authority.
//
// REACHABILITY IS DERIVED, NOT DECLARED. A domain is openable when it has a pill
// on the DomainBar and a body in DomainDashboardScreen; an agent is openable when
// a card for it is actually rendered on some screen. That is why DOCTOR reports
// NO ROUTE while existing perfectly legitimately in the runtime: the surface for
// it has not been built. Reporting that is the point of the pass.
// ─────────────────────────────────────────────────────────────────────────────

import {
  CODE_TEAM,
  CODE_TEAM_EXCLUDED_DOMAINS,
  DOMAINS,
  SERVICE_AGENTS,
  getDomain,
} from '../../lib/agents';

/** The home screen's identity in the runtime registry. App.tsx:252-253. */
export const HOME_DOMAIN_KEY = 'HOMEPAGE';
export const HOME_AGENT_KEY = 'REXY';

export type GraphNavIntent =
  | { kind: 'home' }
  | { kind: 'domain'; domain: string }
  | { kind: 'agent'; domain: string; agent: string };

export const ROUTE_HOME = 'home';

export function domainRoute(domainKey: string): string {
  return domainKey === HOME_DOMAIN_KEY ? ROUTE_HOME : 'domain:' + domainKey;
}

export function agentRoute(domainKey: string, agentName: string): string {
  return 'agent:' + domainKey + '/' + agentName;
}

/**
 * Parse an address back into the intent the App can act on. Returns null for
 * anything unrecognised rather than guessing — an OPEN that cannot be resolved
 * must do nothing visible, not navigate somewhere approximate.
 */
export function parseRoute(route: string): GraphNavIntent | null {
  if (!route) return null;
  if (route === ROUTE_HOME) return { kind: 'home' };
  if (route.indexOf('domain:') === 0) {
    const domain = route.slice(7);
    return domain ? { kind: 'domain', domain } : null;
  }
  if (route.indexOf('agent:') === 0) {
    const rest = route.slice(6);
    const slash = rest.indexOf('/');
    if (slash <= 0 || slash === rest.length - 1) return null;
    return { kind: 'agent', domain: rest.slice(0, slash), agent: rest.slice(slash + 1) };
  }
  return null;
}

// ── reachability ─────────────────────────────────────────────────────────────

/**
 * The domains DomainDashboardScreen.renderBody has a case for. That switch is
 * private to the component, so this is the one place the coupling is written
 * down — and `domainScreenCoverage()` below asserts it against the roster the
 * DomainBar actually renders, so a case added or dropped there surfaces as a
 * parity issue instead of as a dead pill.
 */
const DOMAIN_SCREEN_KEYS: string[] = [
  'CODE', 'SECURITY', 'PERSONAL', 'TRADING', 'BUSINESS', 'RESEARCH', 'GROWTH', 'FRONTIER', 'MEDIA',
];

export function domainScreenCoverage(): { missingBody: string[]; missingPill: string[] } {
  const pills = DOMAINS.map((d) => d.id);
  return {
    missingBody: pills.filter((id) => DOMAIN_SCREEN_KEYS.indexOf(id) < 0),
    missingPill: DOMAIN_SCREEN_KEYS.filter((id) => pills.indexOf(id) < 0),
  };
}

/**
 * HOMEPAGE is openable through the home screen — it has no DomainBar pill and
 * DomainDashboardScreen returns null for it (getDomain is undefined), which is
 * exactly why it is answered here rather than falling through to the roster
 * check and being reported as unreachable.
 */
export function domainRouteExists(domainKey: string): boolean {
  if (domainKey === HOME_DOMAIN_KEY) return true;
  return !!getDomain(domainKey) && DOMAIN_SCREEN_KEYS.indexOf(domainKey) >= 0;
}

/**
 * An agent is openable when a card for it is rendered somewhere a pointer can
 * reach: its own domain's grid, the always-visible service tier, or the code
 * team overlay on the domains that show it (DomainDashboardScreen, Flag 7).
 * REXY is openable through the homepage CHAT tab, which is bound to the
 * {HOMEPAGE, REXY} slice directly (App.tsx rexyChatProps).
 *
 * Agent ids in the frontend roster are lowercase; the runtime names are upper.
 * The comparison is case-folded HERE and nowhere else, so no caller has to know.
 */
export function agentRouteExists(domainKey: string, agentName: string): boolean {
  const agent = agentName.toLowerCase();
  if (domainKey === HOME_DOMAIN_KEY) return agent === HOME_AGENT_KEY.toLowerCase();

  const domain = getDomain(domainKey);
  if (!domain) return false;
  if (domain.agents.some((a) => a.id === agent)) return true;
  if (SERVICE_AGENTS.some((a) => a.id === agent)) return true;
  const codeTeamVisible = (CODE_TEAM_EXCLUDED_DOMAINS as readonly string[]).indexOf(domainKey) < 0;
  if (codeTeamVisible && CODE_TEAM.some((a) => a.id === agent)) return true;
  return false;
}

/**
 * Verify an address the way the app would act on it — parse, then ask the
 * reachability question the parsed intent implies. String comparison against a
 * list of expected routes would only prove the adapter agrees with itself.
 */
export function routeResolves(route: string | undefined): boolean {
  if (!route) return false;
  const intent = parseRoute(route);
  if (!intent) return false;
  if (intent.kind === 'home') return true;
  if (intent.kind === 'domain') return domainRouteExists(intent.domain);
  return agentRouteExists(intent.domain, intent.agent);
}

// ── presentation, for the parity report only ─────────────────────────────────

/**
 * The app's own accent for a domain (lib/agents ACCENTS via DomainDef). Exported
 * for COLOUR PARITY reporting and the Dev readout — NOT for the renderer, which
 * takes every colour from graphPalette. Returns undefined for a domain the app
 * roster does not carry, which is itself the finding.
 */
export function osAccentForDomain(domainKey: string): string | undefined {
  const d = getDomain(domainKey);
  return d ? d.accent : undefined;
}

/** The frontend roster, for drift reporting. Never used to build graph nodes. */
export function frontendRoster(): { domain: string; agents: string[] }[] {
  return DOMAINS.map((d) => ({ domain: d.id, agents: d.agents.map((a) => a.label) }));
}

/**
 * Does the frontend list this agent as NATIVE to this domain — a card in the
 * domain's own grid — as opposed to reaching it through the always-visible
 * service tier or the code-team overlay?
 *
 * The distinction matters: RESEARCH's agents are native in the runtime and reach
 * the screen only as cross-domain services in the app, so they are openable
 * while the domain's own grid reads empty. `agentRouteExists` answers "can it be
 * opened"; this answers "does the app agree about where it lives".
 */
export function frontendListsAgentNatively(domainKey: string, agentName: string): boolean {
  const domain = getDomain(domainKey);
  if (!domain) return false;
  const agent = agentName.toLowerCase();
  return domain.agents.some((a) => a.id === agent);
}

export function frontendServiceAgents(): string[] {
  return SERVICE_AGENTS.map((a) => a.label);
}

export function frontendCodeTeam(): string[] {
  return CODE_TEAM.map((a) => a.label);
}
